import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Select } from 'src/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import {
  useBackfillPulseMutation,
  useGetPulseQuery,
  usePausePulseMutation,
  useResumePulseMutation,
  useRunPulseNowMutation,
  useUpdatePulseSourcesMutation,
} from './pulse.endpoints';
import {
  formatDirectionDelta,
  formatGeography,
  formatPositionValue,
  formatRelativeTime,
  formatVertical,
  needsLookBanner,
  pulseSurveyPath,
  statusBadgeClass,
} from './pulse.helpers';
import {
  PULSE_BACKFILL_OPTIONS,
  PulseEnabledSources,
  PulseSnapshotSummary,
} from './pulse.types';

function PillarCard({
  label,
  snapshot,
  placeholder,
  highlighted,
}: {
  label: string;
  snapshot: PulseSnapshotSummary | null | undefined;
  placeholder?: string;
  highlighted?: boolean;
}) {
  const delta = formatDirectionDelta(snapshot?.direction, snapshot ?? null);
  const toneClass =
    delta.tone === 'up'
      ? 'text-emerald-600'
      : delta.tone === 'down'
        ? 'text-red-600'
        : 'text-gray-500';

  return (
    <div
      className={`rounded-lg border bg-white p-4 ${
        highlighted ? 'border-red-300' : placeholder ? 'border-dashed border-gray-300' : 'border-gray-200'
      }`}
    >
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      {snapshot ? (
        <>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-semibold text-gray-900">
              {formatPositionValue(snapshot)}
            </span>
            {delta.label ? <span className={`text-sm ${toneClass}`}>{delta.label}</span> : null}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {snapshot.confidence_tier.replaceAll('_', ' ')}
            {snapshot.denominator != null ? ` · ${snapshot.denominator} ${snapshot.denominator_unit || ''}` : ''}
          </div>
        </>
      ) : (
        <>
          <div className="mt-1 text-lg font-medium text-gray-400">
            {placeholder || 'Not computed'}
          </div>
          <div className="mt-1 text-xs text-gray-500">No snapshot for this pillar yet</div>
        </>
      )}
    </div>
  );
}

const PulseDetailTab = () => {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId?: string }>();
  const [searchParams] = useSearchParams();
  const pulseId = searchParams.get('id') || '';

  const { data: pulse, isLoading, isError } = useGetPulseQuery(
    { id: pulseId },
    { skip: !pulseId },
  );
  const [pausePulse, { isLoading: pausing }] = usePausePulseMutation();
  const [resumePulse, { isLoading: resuming }] = useResumePulseMutation();
  const [runNow, { isLoading: running }] = useRunPulseNowMutation();
  const [backfillPulse, { isLoading: backfilling }] = useBackfillPulseMutation();
  const [updateSources, { isLoading: savingSources }] = useUpdatePulseSourcesMutation();

  const [backfillOpen, setBackfillOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [backfillMonths, setBackfillMonths] = useState('3');
  const [sourcesJson, setSourcesJson] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const alertMessage = useMemo(
    () => needsLookBanner(pulse?.pillars.position?.provenance),
    [pulse?.pillars.position?.provenance],
  );

  const openSourcesEditor = () => {
    setSourcesJson(JSON.stringify(pulse?.enabled_sources ?? {}, null, 2));
    setSourcesOpen(true);
  };

  const handleSaveSources = async () => {
    if (!pulse) return;
    setActionError(null);
    try {
      const enabled_sources = JSON.parse(sourcesJson) as PulseEnabledSources;
      await updateSources({ id: pulse.id, enabled_sources }).unwrap();
      setSourcesOpen(false);
    } catch (err: unknown) {
      const message =
        (err as { data?: { status?: { status_message?: string } } })?.data?.status
          ?.status_message || 'Unable to update sources.';
      setActionError(typeof message === 'string' ? message : 'Unable to update sources.');
    }
  };

  if (!pulseId) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-red-600">Missing pulse id.</p>
        <Button variant="outline" size="med" onClick={() => navigate(pulseSurveyPath('pulse', clientId))}>
          Back to pulses
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading pulse…</p>;
  }

  if (isError || !pulse) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-red-600">Unable to load pulse.</p>
        <Button variant="outline" size="med" onClick={() => navigate(pulseSurveyPath('pulse', clientId))}>
          Back to pulses
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <button
            type="button"
            className="text-sm text-gray-500 hover:text-gray-800"
            onClick={() => navigate(pulseSurveyPath('pulse', clientId))}
          >
            ← Pulses
          </button>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold text-gray-900">{pulse.name}</h1>
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium uppercase ${statusBadgeClass(
                pulse.status,
              )}`}
            >
              {pulse.status}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {formatVertical(pulse.vertical)} · {formatGeography(pulse.geography_scope)} ·{' '}
            {pulse.cadence === 'weekly' ? 'Weekly' : 'Daily'} · construct v{pulse.construct.version},
            frozen {formatRelativeTime(pulse.construct.frozen_at)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="med" onClick={openSourcesEditor}>
            Edit sources
          </Button>
          {pulse.status === 'active' ? (
            <Button
              variant="outline"
              size="med"
              disabled={pausing}
              onClick={() => pausePulse({ id: pulse.id })}
            >
              Pause
            </Button>
          ) : (
            <Button
              variant="outline"
              size="med"
              disabled={resuming}
              onClick={() => resumePulse({ id: pulse.id })}
            >
              Resume
            </Button>
          )}
          <Button variant="outline" size="med" onClick={() => setBackfillOpen(true)}>
            Backfill
          </Button>
          <Button variant="black" size="med" disabled={running} onClick={() => runNow({ id: pulse.id })}>
            {running ? 'Queuing…' : 'Run now'}
          </Button>
        </div>
      </div>

      {actionError ? <p className="text-sm text-red-600">{actionError}</p> : null}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <PillarCard label="Salience" snapshot={pulse.pillars.salience} />
        <PillarCard label="Climate" snapshot={pulse.pillars.climate} placeholder="Not computed" />
        <PillarCard label="Expectation" snapshot={pulse.pillars.expectation} />
        <PillarCard label="Position" snapshot={pulse.pillars.position} highlighted={Boolean(alertMessage)} />
      </div>

      {alertMessage ? (
        <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-amber-800">
              Needs a look
            </div>
            <p className="mt-1 text-sm text-amber-900">{alertMessage}</p>
          </div>
        </div>
      ) : null}

      <section className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Run history</h2>
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50 hover:bg-gray-50">
                <TableHead>Window</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Source outcomes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pulse.recent_runs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-gray-500">
                    No runs yet.
                  </TableCell>
                </TableRow>
              )}
              {pulse.recent_runs.map((run) => {
                const outcomeKeys = Object.keys(run.source_outcomes || {});
                return (
                  <TableRow key={run.id}>
                    <TableCell className="text-sm">
                      {new Date(run.window_start).toLocaleDateString()} –{' '}
                      {new Date(run.window_end).toLocaleDateString()}
                      {run.is_backfill ? ' · backfill' : ''}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {formatRelativeTime(run.started_at ?? run.scheduled_for)}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs capitalize text-gray-700">
                        {run.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {run.duration_seconds != null ? `${run.duration_seconds}s` : '—'}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {outcomeKeys.length
                        ? `${outcomeKeys.length} source${outcomeKeys.length === 1 ? '' : 's'}`
                        : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Source health
        </h2>
        <div className="flex flex-wrap gap-2">
          {pulse.source_health.length === 0 && (
            <span className="text-sm text-gray-500">No source outcomes yet.</span>
          )}
          {pulse.source_health.map((chip) => (
            <span
              key={chip.source_key}
              className={`inline-flex rounded-full border px-3 py-1 text-xs ${
                String(chip.status).toLowerCase().includes('degrad') ||
                String(chip.status).toLowerCase().includes('fail')
                  ? 'border-rose-200 bg-rose-50 text-rose-800'
                  : 'border-amber-100 bg-amber-50 text-amber-900'
              }`}
            >
              {chip.source_key} · {chip.status}
              {chip.items != null ? ` · ${chip.items} items` : ''}
            </span>
          ))}
        </div>
      </section>

      <Dialog open={backfillOpen} onOpenChange={setBackfillOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Queue backfill</DialogTitle>
          </DialogHeader>
          <label className="space-y-1 text-sm">
            <span className="text-gray-600">Lookback</span>
            <Select
              className="w-full"
              value={backfillMonths}
              onValueChange={setBackfillMonths}
              options={PULSE_BACKFILL_OPTIONS.filter((opt) => opt.value > 0).map((opt) => ({
                value: String(opt.value),
                label: opt.label,
              }))}
            />
          </label>
          <DialogFooter>
            <Button variant="outline" size="med" onClick={() => setBackfillOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="black"
              size="med"
              disabled={backfilling}
              onClick={async () => {
                await backfillPulse({ id: pulse.id, months: Number(backfillMonths) });
                setBackfillOpen(false);
              }}
            >
              {backfilling ? 'Queuing…' : 'Queue backfill'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sourcesOpen} onOpenChange={setSourcesOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit sources</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">
            Update the enabled_sources JSON. Unknown source keys are rejected by the API.
          </p>
          <textarea
            className="min-h-64 w-full rounded-md border border-gray-200 bg-white px-3 py-2 font-mono text-xs"
            value={sourcesJson}
            onChange={(event) => setSourcesJson(event.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" size="med" onClick={() => setSourcesOpen(false)}>
              Cancel
            </Button>
            <Button variant="black" size="med" disabled={savingSources} onClick={handleSaveSources}>
              {savingSources ? 'Saving…' : 'Save sources'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PulseDetailTab;
